import {
  BURN_METHOD_LABELS,
  DEFAULT_COLLECTIBLE_NAME,
  EXCLUSION_SCOPES,
  EXCLUSION_TYPES,
  MOV_TYPE_LABELS,
  TEXT_TRANSFORM,
} from "./constants";
import { getProviderProfile, providerPublishesProgram } from "./provider";
import { isPointProgram } from "./rewards";
import { savedDateOrNull } from "../helpers/dateTimeHelper";
import type { CoreCopy } from "../copy/resolve";
import type { MoneyFormat } from "../utils/currency/format";
import type {
  ExcludableItem,
  LoyaltyProgram,
  ProfileFieldKey,
  ProgramDetailsEnvelope,
  ProgramExclusion,
} from "./types";

/**
 * Rules over a loyalty PROGRAM — the merchant's configuration of how points convert, what they
 * are called, what they cannot be spent on, and whether enrolment needs an OTP.
 *
 * Pure. Everything takes the program (or the envelope around it) as an argument, so the same
 * rules serve the POS panel, the enrolment screen, the kiosk and mobile.
 *
 * ⚠️ Programs are cached PER CHANNEL (`programDetailsMap[channelDefId]`) and only the
 * program-driven partner ever has one. Callers that mix a program read with a provider decision
 * must pair the two — `skipsEnrollmentOtp` below is the example, and the reason it takes both.
 */

/** The program itself, or `null`. Both "no entry" and "negative envelope" answer `null`. */
export const getProgram = (programDetails?: ProgramDetailsEnvelope | null): LoyaltyProgram | null =>
  programDetails?.data ?? null;

/** Case a label according to a `TEXT_TRANSFORM` value. Unknown transforms pass through. */
export function applyTextTransform(str: string, transform?: string): string {
  switch (transform) {
    case TEXT_TRANSFORM.UPPERCASE:
      return str.toUpperCase();
    case TEXT_TRANSFORM.LOWERCASE:
      return str.toLowerCase();
    case TEXT_TRANSFORM.CAPITALIZE:
      return str.replace(/\b\w/g, (c) => c.toUpperCase());
    case TEXT_TRANSFORM.FIRST_UPPER:
      return str.charAt(0).toUpperCase() + str.slice(1);
    default:
      return str;
  }
}

/**
 * What this program calls its collectible, singular or plural for `count`.
 *
 * `count` omitted means the plural ("Apply points"), matching how the copy reads when no number
 * is on screen. A program that names a singular but no plural gets a naive `+ "s"` — that is the
 * app's behaviour and changing it would rename live merchants' collectibles.
 */
export function getCollectibleLabel(
  program: LoyaltyProgram | null | undefined,
  count?: number,
  transform?: string,
): string {
  const collectibleName = program?.collectibleName || DEFAULT_COLLECTIBLE_NAME;
  const collectibleNamePlural = program?.collectibleNamePlural;
  const label = count === 1 ? collectibleName : collectibleNamePlural || `${collectibleName}s`;
  return transform ? applyTextTransform(label, transform) : label;
}

/** The burn configuration, with its labels resolved. `null` program answers zeroed defaults. */
export function getBurnRules(program: LoyaltyProgram | null | undefined) {
  const { burnValue, burnMethod, burnMovType, burnCap, burnMinOrderValue } = program ?? {};
  return {
    burnValue: burnValue ?? 0,
    burnCap: burnCap ?? 0,
    minOrderValue: burnMinOrderValue ?? 0,
    /** Human label for what `burnValue` is a percentage of. */
    methodLabel: BURN_METHOD_LABELS[burnMethod as string] || "order value",
    /** Human label for how `minOrderValue` is measured. Empty when unset. */
    movLabel: MOV_TYPE_LABELS[burnMovType as string] || "",
  };
}

/**
 * The "you can redeem up to…" tooltip.
 *
 * `money` is an ARGUMENT rather than a lookup: resolving a symbol and a precision reads business
 * settings, and this module stays pure so the same sentence can be built on a server, in a test,
 * or on a platform whose currency helper differs.
 *
 * It is the whole FORMATTER, not a symbol, so this cannot paste a raw number beside one — the cap
 * reads `₹500.00` like the bill it caps, never `₹500`. See `RedemptionPanelContext.money`.
 *
 * Answers `""` when there is nothing to promise (no program, or no percentage cap configured) —
 * callers render no tooltip for an empty string.
 */
export function buildRedemptionTooltip(
  program: LoyaltyProgram | null | undefined,
  maxRedeemableAmount: number,
  money: MoneyFormat,
  copy: CoreCopy,
): string {
  if (!program) return "";
  const { burnValue, methodLabel, movLabel } = getBurnRules(program);
  if (!burnValue) return "";

  // Built by wrapping rather than by four keys: both clauses are optional and independent, so
  // each one has to attach to whatever is there so far. `${base}` is the seam — the same shape
  // `blockedReason` in the kiosk's points card uses.
  let text = copy("loyalty.tooltip.burn", { percent: burnValue, method: methodLabel });
  if (movLabel) text = copy("loyalty.tooltip.mov", { base: text, mov: movLabel });
  if (maxRedeemableAmount)
    text = copy("loyalty.tooltip.max", { base: text, amount: money(maxRedeemableAmount) });
  return text;
}

/**
 * Exclusions that apply to REDEMPTION, grouped by what they exclude.
 *
 * ⚠️ Earn-scope entries are dropped. A program may exclude an item from earning points without
 * excluding it from being paid for with them, and showing the earn list under "cannot redeem
 * against" would be wrong.
 */
export function groupRedemptionExclusions(program: LoyaltyProgram | null | undefined) {
  const items: ProgramExclusion[] = [];
  const categories: ProgramExclusion[] = [];
  const subCategories: ProgramExclusion[] = [];

  for (const exclusion of program?.exclusions ?? []) {
    if (exclusion?.scope === EXCLUSION_SCOPES.EARN) continue;
    if (exclusion?.type === EXCLUSION_TYPES.ITEM) items.push(exclusion);
    else if (exclusion?.type === EXCLUSION_TYPES.CATEGORY) categories.push(exclusion);
    else if (exclusion?.type === EXCLUSION_TYPES.SUB_CATEGORY) subCategories.push(exclusion);
  }

  return {
    items,
    categories,
    subCategories,
    hasAny: !!(items.length || categories.length || subCategories.length),
  };
}

/**
 * Whether enrolment consent can be recorded without an OTP.
 *
 * ⚠️ **The provider check is not optional.** `programDetailsMap` is keyed by CHANNEL, not by
 * provider, so a cached programme can outlive a provider change and be read while a partner that
 * has no programme at all is active. A provider whose profile does not honour the flag therefore
 * always takes the OTP path, however the cached programme is configured. This pairing was spelled
 * inline in the customer modal; it lives here so the next caller cannot forget half of it.
 */
export const skipsEnrollmentOtp = (
  providerMetaId?: string,
  programDetails?: ProgramDetailsEnvelope | null,
): boolean =>
  getProviderProfile(providerMetaId).honoursSkipEnrollmentOtp &&
  !!getProgram(programDetails)?.skipEnrollmentOtp;

/**
 * Is this item barred from being paid for with points?
 *
 * A programme may exclude an item, its sub-category or its category. Only providers whose profile
 * declares `supportsItemExclusions` have the concept at all — for everyone else nothing is
 * excluded, and the rule says so without reading a programme that may belong to another provider.
 *
 * ⚠️ Earn-scope exclusions are skipped: a programme can bar an item from EARNING points without
 * barring it from being paid for with them. Same distinction as `groupRedemptionExclusions`.
 *
 * 🔴 **An ITEM entry matches on its `skuCode`, never its `id`.** Category and sub-category entries
 * carry their key in `id`; an item entry carries its sku in `skuCode`. v1's cart line
 * (`release_4.0.35`) compared `skuCode`. DP-8174 moved the rule here comparing `id`, and until
 * 2026-09-17 no item-level exclusion was marked in a v2 cart on either platform.
 */
export function isItemExcludedFromRedemption(
  item: ExcludableItem | null | undefined,
  program: LoyaltyProgram | null | undefined,
  providerMetaId?: string,
): boolean {
  if (!getProviderProfile(providerMetaId).supportsItemExclusions) return false;
  const exclusions = program?.exclusions;
  if (!Array.isArray(exclusions) || !exclusions.length || !item) return false;

  return exclusions.some((exclusion) => {
    if (exclusion?.scope === EXCLUSION_SCOPES.EARN) return false;
    switch (Number(exclusion?.type)) {
      case EXCLUSION_TYPES.CATEGORY:
        return !!item.categoryId && exclusion?.id === item.categoryId;
      case EXCLUSION_TYPES.SUB_CATEGORY:
        return !!item.subCategoryId && exclusion?.id === item.subCategoryId;
      case EXCLUSION_TYPES.ITEM:
        return !!item.skuCode && exclusion?.skuCode === item.skuCode;
      default:
        return false;
    }
  });
}

/**
 * The payment modes a programme bars while redeemed points are on the bill, lowercased.
 *
 * Two groups are merged: `burn` (barred when spending points) and `both`. **`earn` is
 * deliberately ignored** — a mode barred from ACCRUING points says nothing about settling a bill
 * that already carries a points discount, and folding it in here would block modes the merchant
 * never restricted.
 *
 * ⚠️ **The provider check is not optional**, for the same reason it is not optional on
 * `skipsEnrollmentOtp` and `pointsToAmount`: `programDetailsMap` is keyed by CHANNEL, so a
 * programme cached while one partner was active is still readable under another. Unpaired, this
 * would apply the program-driven partner's restrictions to an ordinary points partner's
 * redemption — a payment mode the cashier is entitled to, greyed out with no way to explain it.
 */
export function getExcludedBurnPaymentModes(
  providerMetaId?: string,
  programDetails?: ProgramDetailsEnvelope | null,
): string[] {
  if (!providerPublishesProgram(providerMetaId)) return [];
  const excluded = getProgram(programDetails)?.excludedPaymentModes;
  return [...(excluded?.burn ?? []), ...(excluded?.both ?? [])]
    .filter((mode): mode is string => typeof mode === "string" && !!mode)
    .map((mode) => mode.toLowerCase());
}

/**
 * Split a payment-method list into the modes that may settle this bill and the modes the
 * programme bars because points are applied to it.
 *
 * Both halves are returned rather than just the usable ones: a mode that silently vanishes from
 * the grid reads as a terminal misconfiguration, so every surface shows the barred ones disabled
 * under `EXCLUDED_PAYMENT_MODES_NOTICE` instead. It also means a bill where EVERY mode is barred
 * still explains itself rather than falling through to "no payment modes allowed".
 *
 * 🔴 **Nothing is barred unless points are actually on the bill.** The restriction is a property
 * of the redemption, not of the programme — the same programme leaves every mode available on a
 * bill with no points applied.
 *
 * Order within each half is the caller's own, so paging and auto-selection keep working off the
 * `allowed` list unchanged.
 */
export function partitionPaymentMethodsForRedemption<T extends { mode?: string }>(
  paymentMethods: T[] | null | undefined,
  {
    isPointsApplied,
    providerMetaId,
    programDetails,
  }: {
    isPointsApplied: boolean;
    providerMetaId?: string;
    programDetails?: ProgramDetailsEnvelope | null;
  },
): { allowed: T[]; excluded: T[] } {
  const methods = paymentMethods ?? [];
  if (!isPointsApplied) return { allowed: methods, excluded: [] };

  const excludedModes = getExcludedBurnPaymentModes(providerMetaId, programDetails);
  if (!excludedModes.length) return { allowed: methods, excluded: [] };

  const isBarred = (method: T) => excludedModes.includes((method?.mode ?? "").toLowerCase());
  return {
    allowed: methods.filter((method) => !isBarred(method)),
    excluded: methods.filter(isBarred),
  };
}

/**
 * Is there a live programme worth showing the customer's loyalty standing against?
 *
 * A provider with no programme has nothing to gate on and always answers `true`; one that
 * publishes a programme needs it present. Read by the customer summary — its balance banner, and
 * whether "View History" is offered for a customer new to Rista. The web cart's chip asks the
 * narrower `canShowPointsStanding` below.
 */
export const canShowLoyaltyStanding = (
  providerMetaId?: string,
  programDetails?: ProgramDetailsEnvelope | null,
): boolean => !providerPublishesProgram(providerMetaId) || hasProgram(programDetails);

/**
 * May the web cart's customer chip quote the standing — "240 Points available", or "2 rewards
 * available"? Both count in points, so a provider that publishes a programme needs a POINT one;
 * a provider with no programme always may.
 *
 * ⚠️ **Narrower than `canShowLoyaltyStanding` on purpose — each is v1's own answer.** v1's cart
 * required `programType === "point"`; v1's summary only asked for a programme. DP-8174 put both
 * on the summary's rule, so a `visit` programme's chip quoted "Points" the POS cannot redeem
 * (`canRedeemPointsOnPos` asks the same type). Split again 2026-09-25. A point programme is a
 * programme, so this is never true where `canShowLoyaltyStanding` is false.
 */
export const canShowPointsStanding = (
  providerMetaId?: string,
  programDetails?: ProgramDetailsEnvelope | null,
): boolean => !providerPublishesProgram(providerMetaId) || isPointProgram(programDetails);

/** Is there a live program for this channel? Used to decide whether to offer "View program". */
export const hasProgram = (programDetails?: ProgramDetailsEnvelope | null): boolean =>
  !!getProgram(programDetails);

const PROFILE_FIELDS: ProfileFieldKey[] = ["dateOfBirth", "anniversary", "email", "gender"];

/** `true` only when the CRM says the merchant has a profile config set up. */
export const isProfileConfigured = (programDetails?: ProgramDetailsEnvelope | null): boolean =>
  programDetails?.profileConfig?.configured === true;

/** Profile fields the CRM config shows on POS. No config shows all; only `"hidden"` hides. */
export const getVisibleProfileFields = (
  programDetails?: ProgramDetailsEnvelope | null,
): ProfileFieldKey[] =>
  PROFILE_FIELDS.filter(
    (field) => programDetails?.profileConfig?.data?.fields?.[field]?.visibility !== "hidden",
  );

/**
 * Profile fields that can be fed only once: a birthday or anniversary already on the customer's
 * SAVED record is locked. Pass the stored customer, never the form — a date picked in this session
 * stays editable until it is saved. A half-picked value is not a date, so it does not lock.
 */
export const getLockedProfileFields = (
  customer?: { birthDate?: string | null; anniversaryDate?: string | null } | null,
): ProfileFieldKey[] => {
  const locked: ProfileFieldKey[] = [];
  if (savedDateOrNull(customer?.birthDate)) locked.push("dateOfBirth");
  if (savedDateOrNull(customer?.anniversaryDate)) locked.push("anniversary");
  return locked;
};
